<template>
    <div>
        <v-col cols="12">
            <h2 class="mt-4 mb-0">{{ t('help-pages::page.title.help_pages') }}</h2>

            <b-datatable
                :key="dataTableKey"
                :columns="columns"
                :endpoint="listRoute"
                :hidden-columns="['id']"
                :meta-fields="metaFields"
                has-filter-selector
                item-key="id"
            >
                <template #filters="{ filters }">
                    <v-col cols="4" class="pb-0">
                        <b-filter-input
                            v-model="filters.filters.title"
                            :label="t('help-pages::filters.label.title')"
                            class="name-filter"
                            dense
                            outlined
                        ></b-filter-input>
                    </v-col>
                </template>

                <template #[`item.created_at`]="{ item }">
                    {{ item.created_at ? moment(item.created_at).format($root.dateFormat) : '' }}
                </template>

                <template #[`item.is_active`]="{ item }">
                    <div class="grid-status">
                        <span>
                            {{ item.is_active ? t('default::status.active') : t('default::status.inactive') }}
                        </span>
                    </div>
                </template>
                <template #[`item.acl_routes`]="{ item }">
                    <div v-if="!item.acl_routes.length">
                        <div class="grid-status">
                            <span v-if="!item.acl_routes.length">
                                {{ t('help-pages::label.all_routes') }}
                            </span>
                        </div>
                    </div>
                    <div v-else-if="item.acl_routes.length === 1">
                        <div class="grid-status">
                            <span>
                                {{ item.acl_routes[0].tag + ': ' + item.acl_routes[0].name }}
                            </span>
                        </div>
                    </div>
                    <div v-else>
                        <v-edit-dialog ref="memoDialog" :return-value.sync="item.acl_routes" :transition="null">
                            <div class="has-more">
                                <div class="grid-status">
                                    <span>
                                        {{ item.acl_routes[0].tag + ': ' + item.acl_routes[0].name }}
                                    </span>
                                </div>
                                <span class="has-more-dots">...</span>
                            </div>

                            <template #input>
                                <div class="close-action">
                                    <v-icon color="accent" @click="closeEditDialog">mdi-close</v-icon>
                                </div>
                                <div class="content">
                                    <div v-for="route in item.acl_routes" :key="route.id" class="grid-status">
                                        <span>
                                            {{ route.tag + ': ' + route.name }}
                                        </span>
                                    </div>
                                </div>
                            </template>
                        </v-edit-dialog>
                    </div>
                </template>
                <template #[`item.roles`]="{ item }">
                    <div class="grid-status">
                        <span v-if="!item.roles.length">
                            {{ t('help-pages::label.all_roles') }}
                        </span>
                        <span v-for="route in item.acl_routes" v-else :key="route.id">
                            {{ role.name }}
                        </span>
                    </div>
                </template>
                <template #[`item.actions`]="{ item }">
                    <div class="table-actions">
                        <v-tooltip v-if="can('api.v1.help-pages.update')" top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn color="disabled-color" icon v-bind="attrs" :href="getItemUrl(item)" v-on="on">
                                    <v-icon>mdi-pencil</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('default::button.edit') }}</span>
                        </v-tooltip>
                        <b-delete
                            v-if="can('api.v1.help-pages.destroy')"
                            :path="deleteItemUrl(item)"
                            :text="t('default::confirmation.deletion')"
                            :title="t('default::confirmation.general')"
                            icon="mdi-trash-can"
                            @deleted="triggerDeleted"
                        >
                        </b-delete>
                    </div>
                </template>

                <!-- <template #[`body.append`]="data">
                    <tr class="totals">
                        <th class="empty" colspan="2"></th>
                        <th :class="data.tdTotalsClass">
                            {{ t('help-pages::grid.meta.grand_total') }} :
                            {{ data.metaData.count }}
                        </th>
                        <th :class="data.tdTotalsClass">
                            {{ t('help-pages::grid.meta.active') }} :
                            {{ data.metaData.active_pages_count }}
                        </th>
                        <th :class="data.tdTotalsClass">
                            {{ t('help-pages::grid.meta.inactive') }} :
                            {{ data.metaData.inactive_pages_count }}
                        </th>
                    </tr>
                </template> -->

                <template #buttons>
                    <div class="px-2">
                        <v-tooltip top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    v-if="can('help-pages.create')"
                                    color="primary"
                                    icon
                                    v-bind="attrs"
                                    :href="getCreateUrl()"
                                    v-on="on"
                                >
                                    <v-icon>mdi-plus-circle</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('help-pages::button.new_help_article') }}</span>
                        </v-tooltip>
                    </div>
                </template>
            </b-datatable>
        </v-col>
    </div>
</template>

<script>
export default {
    props: {
        listRoute: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            metaFields: ['count', 'active_pages_count', 'inactive_pages_count'],
            columns: [
                {
                    text: t('default::grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('help-pages::grid.label.title'),
                    value: 'title',
                    width: 200,
                    sortable: true,
                },
                {
                    text: t('help-pages::grid.label.data_added'),
                    value: 'created_at',
                    width: 160,
                    sortable: true,
                },
                {
                    text: t('help-pages::grid.label.pages'),
                    value: 'acl_routes',
                    fields: 'acl_routes',
                    width: 300,
                    sortable: false,
                },
                {
                    text: t('help-pages::grid.label.active'),
                    value: 'is_active',
                    width: 200,
                    sortable: true,
                },
                {
                    text: t('help-pages::grid.label.actions'),
                    value: 'actions',
                    sortable: false,
                    fields: null,
                    align: 'end',
                    width: 140,
                },
            ],
            dataTableKey: 0,
        };
    },
    created() {
        // this.export()
    },
    methods: {
        export() {
            axios
                .get(
                    route('api.v1.help-pages.index', {
                        fields: '*,acl_routes.system_name,acl_routes.*',
                        limit: -1,
                    })
                )
                .then((res) => {
                    console.warn(res.data.data);
                });
        },
        getCreateUrl() {
            return route('help-pages.create');
        },
        getItemUrl(item) {
            return route('help-pages.get', {
                help_page: item.id,
                fields: 'id,acl_routes,roles,title,content,is_active',
            });
        },
        deleteItemUrl(item) {
            return route('api.v1.help-pages.destroy', { help_page: item.id });
        },
        triggerDeleted() {
            this.dataTableKey++;
            this.$root.notify('info', t('default::notification.success'), t('help-pages::notification.deleted'));
        },
        closeEditDialog() {
            const event = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            this.$el.dispatchEvent(event);
        },
    },
};
</script>
